import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomMediaUpload from '../../../../shared-components/custom-media-upload/CustomMediaUpload';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import deviceApi from '../../../../utils/api/device-hub';

interface AddModelDrawerProperties {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /**
   * Called after a model is successfully created.
   * @param brandId - The brand that the model was added under
   * @param modelId - The _id of the newly created model
   * @param models - Updated list of model options for the selected brand
   */
  onModelCreated: (
    brandId: string,
    modelId: string,
    models: { value: string; label: string }[],
  ) => void;
}

interface BrandsListInterface {
  companyName?: string;
  name?: string;
  _id: string;
}

/**
 * Slide-in drawer modal for adding a new device model.
 * @param {object} properties - The properties object.
 * @param {boolean} properties.open - Whether the modal is open.
 * @param {Function} properties.onClose - Callback to close the modal.
 * @param {Function} properties.onModelCreated - Callback called after a model is successfully created.
 * @returns {React.ReactElement} The rendered modal component.
 */
function AddModelDrawer({ open, onClose, onModelCreated }: Readonly<AddModelDrawerProperties>) {
  const [modelName, setModelName] = useState('');
  const [brandSelect, setBrandSelect] = useState('');
  const [brandError, setBrandError] = useState('');
  const [modelNameError, setModelNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation('station');
  const [modelMedia, setModelMedia] = useState<File[]>([]);
  const [brandsList, setBrandsList] = useState<BrandsListInterface[]>([]);

  const brandOptions = useMemo(
    () =>
      brandsList.map((brand) => ({
        value: brand._id,
        label: brand.name || brand.companyName || '',
      })),
    [brandsList],
  );

  /**
   * Resets all form fields to their initial state.
   */
  const resetForm = () => {
    setModelName('');
    setBrandSelect('');
    setModelMedia([]);
    setBrandError('');
    setModelNameError('');
    setShowFormErrors(false);
  };

  /**
   * Handles the change event for the media upload.
   * @param {string} name - The name of the input field.
   * @param {string | number | File[] | boolean} changeValue - The selected files or values.
   */
  const handleMediaChange = (name: string, changeValue: string | number | File[] | boolean) => {
    if (name === 'medias') {
      setModelMedia(changeValue as File[]);
    }
  };

  /**
   * Handles closing the modal and resetting the form.
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Validates and submits the model creation form.
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    setBrandError('');
    setModelNameError('');

    if (!brandSelect) {
      return;
    }
    if (!modelName.trim()) {
      return;
    }

    setSubmitting(true);

    const hasFile = modelMedia.length > 0 && modelMedia[0] instanceof File;
    const hasStringUrl =
      modelMedia.length > 0 &&
      typeof modelMedia[0] === 'string' &&
      (modelMedia[0] as unknown as string).trim() !== '';

    let apiCall: Promise<any>;

    if (hasFile) {
      const formData = new FormData();
      formData.append('name', modelName.trim());
      formData.append('brandId', brandSelect);
      formData.append('modelImage', modelMedia[0]);
      apiCall = deviceApi.createModel(formData, true);
    } else {
      const payload: { name: string; brandId: string; imageUrl?: string } = {
        name: modelName.trim(),
        brandId: brandSelect,
      };
      if (hasStringUrl) {
        payload.imageUrl = modelMedia[0] as unknown as string;
      }
      apiCall = deviceApi.createModel(payload);
    }

    apiCall
      .then((response) => {
        // Reload models for the selected brand and pass back to parent
        const selectedBrandOption = brandOptions.find((b) => b.value === brandSelect);
        const selectedBrandItem = brandsList.find((b) => b._id === brandSelect);
        const selectedBrandName =
          selectedBrandOption?.label ||
          selectedBrandItem?.name ||
          selectedBrandItem?.companyName ||
          brandSelect;

        deviceApi
          .fetchModels({ brandName: selectedBrandName })
          .then((modelsResponse) => {
            const data = modelsResponse.data?.results || modelsResponse.data || [];
            const models = data.map((m: any) => ({ value: m._id, label: m.name }));

            const newModel = response.data?.results?.[0] || response.data || {};
            const modelId = newModel._id || newModel.id || '';

            const matched = !modelId
              ? models.find((m: any) => m.label.toLowerCase() === modelName.trim().toLowerCase())
              : null;

            onModelCreated(brandSelect, modelId || matched?.value || '', models);
          })
          .catch(() => {
            onModelCreated(brandSelect, '', []);
          });

        setShowSuccess(true);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || error?.message || 'Failed to create model';
        const lowerMessage = message.toLowerCase();
        if (
          lowerMessage.includes('model') ||
          lowerMessage.includes('already exists') ||
          lowerMessage.includes('already taken')
        ) {
          setModelNameError(message);
        } else if (lowerMessage.includes('brand') && !lowerMessage.includes('model')) {
          setBrandError(message);
        } else {
          setModelNameError(message);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  /**
   * Function to fetch brands list.
   * @returns {Promise<void>} Resolves when brands are fetched.
   */
  const fetchBrandsList = async () => {
    try {
      const response = await deviceApi.fetchBrands({
        paginate: false,
        status: ['approved', 'pending'],
        skipGlobal: true,
      });
      const data = response?.data?.results || response?.data || [];
      setBrandsList(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to fetch brands:', error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBrandsList();
    }
  }, [open]);

  return (
    <>
      <ModalBox
        open={open}
        title=""
        content={
          <div className="left-drawer-content">
            <div className="drawer-header">
              <h3 className="drawer-title">Add Model</h3>
              <p className="drawer-subtitle">Create a new model to associate with a brand.</p>
            </div>
            <div className="drawer-body">
              <SelectField
                name="modelBrandSelect"
                inputLabel="Brand"
                options={brandOptions}
                placeholder="Choose Brand"
                selectedValue={brandSelect}
                onChangeValue={(_, value) => {
                  setBrandSelect(value.toString());
                  if (brandError) setBrandError('');
                }}
                error={Boolean((showFormErrors && !brandSelect) || brandError)}
                errorMessage={brandError || 'Brand is required'}
                valueRequired
                enableSearch
              />
              <CustomInput
                type="text"
                name="newModelName"
                inputLabel="Model Name"
                placeholder="Enter Model Name"
                value={modelName}
                onChangeValue={(_, value) => {
                  setModelName(value.toString());
                  if (modelNameError) setModelNameError('');
                }}
                error={Boolean((showFormErrors && !modelName.trim()) || modelNameError)}
                errorMessage={modelNameError || 'Model name is required'}
                valueRequired
                characterLimit={100}
              />
              <CustomMediaUpload
                inputLabel={t('uploadMedia')}
                acceptedFileType=".jpg,.jpeg,.png"
                placeholder={t('uploadMediaPlaceholder')}
                name="medias"
                selectedFiles={modelMedia}
                onChangeValue={handleMediaChange}
                tooltipText={t('mediaModelTooltip')}
                maxImageSizeMB={1}
                maxFileCount={1}
              />
            </div>
            <div className="drawer-footer">
              <MyButton label="Cancel" buttonType="error" onClickFunc={handleClose} />
              <MyButton
                label="Save Model"
                buttonType="submit"
                onClickFunc={handleSubmit}
                disableButton={submitting}
                loading={submitting}
              />
            </div>
          </div>
        }
        closeModal={handleClose}
        header={false}
        position="right"
        modalBoxAnimation="modalAnimateFromRight"
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title="Model Added Successfully"
            description="The model has been added successfully."
            onNotificationClose={() => {
              setShowSuccess(false);
              resetForm();
              onClose();
            }}
          />
        }
        closeModal={() => {
          setShowSuccess(false);
          resetForm();
          onClose();
        }}
        header={false}
        notification
      />
    </>
  );
}

export default AddModelDrawer;
